/**
 * The 11-column location section.
 *
 * Serves two cases:
 *   - RECENT mode (last 5 working days) — the original table, unchanged.
 *   - a RANGE that collapsed to a single week bucket, where the week column
 *     would just repeat the range totals. That case passes `range`, which
 *     switches every window-dependent label to range semantics. Without it the
 *     table would head calendar-range totals as "Last 5 Working Days" and
 *     anchor the appointment note on a "report date" — the numbers would be
 *     right and the labels would lie.
 */

import React from 'react';
import { SalesFunnelRow } from '../../../../services/api';
import {
  SalesTeamLocationSection,
  SalesTeamSectionP,
} from '../../../../types/salesTeamFunnel';
import {
  EmptyTeamRow,
  MemberRow,
  TeamHeaderRow,
  TotalsRow,
  shortDate,
} from '../../../shared/SalesFunnelRows';
import { isInactiveRow } from './funnelWeekHelpers';

/**
 * `Last 5: 7/13–7/18 · Next 5: 7/19–7/23`, or a range summary when the payload
 * carries one. Guarded against short/empty date arrays: shortDate() splits the
 * string, so an undefined entry would throw and blank the whole modal body
 * rather than just this line.
 */
export function windowSummary(
  pastDates: string[],
  nextDates: string[],
  range?: { startDate: string; endDate: string },
): string {
  const span = (dates: string[]) => (dates.length
    ? `${shortDate(dates[0])}–${shortDate(dates[dates.length - 1])}`
    : '—');
  const past = range
    ? `Range: ${shortDate(range.startDate)}–${shortDate(range.endDate)}`
    : `Last 5: ${span(pastDates)}`;
  return `${past} · Next 5: ${span(nextDates)}`;
}

export function funnelRowId(
  locationId: number,
  team: SalesTeamSectionP,
  row: SalesFunnelRow,
): string {
  return `${locationId}#${team.managerId}#${row.vinUserId ?? ''}#${row.dmsSalesRepId ?? ''}#${row.name}`;
}

export const FunnelLocationSection: React.FC<{
  location: SalesTeamLocationSection;
  openRows: Set<string>;
  onToggle: (rowId: string) => void;
  activeLeadsUnavailable: boolean;
  reportDate: string;
  reportDateIsToday: boolean;
  /** Present when a date range collapsed to one week bucket. */
  range?: { startDate: string; endDate: string };
  /** Display-only row filter; applies in range mode only. */
  hideInactive?: boolean;
}> = ({
  location, openRows, onToggle, activeLeadsUnavailable, reportDate, reportDateIsToday,
  range, hideInactive = false,
}) => {
  const summary = windowSummary(location.pastDates, location.nextDates, range);
  const rowId = (team: SalesTeamSectionP, row: SalesFunnelRow) =>
    funnelRowId(location.locationId, team, row);
  // Every window-dependent label flips with the mode.
  const countsGroupLabel = range ? 'Range Total' : 'Last 5 Working Days';
  const apptAnchorLabel = reportDateIsToday
    ? 'includes today'
    : (range ? 'includes end date' : 'includes report date');
  const emptyLabel = range
    ? 'No funnel activity or teams at this location in this date range.'
    : 'No funnel activity or teams at this location in this window.';

  return (
    <div className="mb-8 break-inside-avoid funnel-location-block" style={{ pageBreakInside: 'avoid' }}>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-base font-semibold text-gray-900">{location.locationName}</h3>
        <span className="text-xs text-gray-500">{summary}</span>
      </div>

      {location.teams.length === 0 ? (
        <div className="text-sm text-gray-500 italic border border-gray-200 rounded-lg px-4 py-6 text-center">
          {emptyLabel}
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr className="border-b border-gray-200">
                <th className="px-3 py-1.5" />
                <th colSpan={4} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                  {countsGroupLabel}
                  {range && (
                    <span className="ml-1 font-normal normal-case text-gray-400">
                      ({shortDate(range.startDate)}–{shortDate(range.endDate)})
                    </span>
                  )}
                </th>
                <th colSpan={3} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                  Conversion
                </th>
                <th className="px-3 py-1.5 border-l border-gray-200" />
                <th colSpan={2} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                  Appts Scheduled
                  <span className="ml-1 font-normal normal-case text-gray-400">
                    ({apptAnchorLabel})
                  </span>
                </th>
              </tr>
              <tr>
                <th className="px-3 py-2 text-left">Team / Salesperson</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Leads</th>
                <th className="px-3 py-2 text-right">Visits</th>
                <th className="px-3 py-2 text-right">Appts</th>
                <th className="px-3 py-2 text-right">Sales</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Visits/Lead</th>
                <th className="px-3 py-2 text-right">Sales/Visit</th>
                <th className="px-3 py-2 text-right">Sales/Lead</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Active Leads</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Next 3</th>
                <th className="px-3 py-2 text-right">Next 5</th>
              </tr>
            </thead>
            <tbody>
              {location.teams.map((team) => {
                // Filtering is display-only — the subtotals below still come
                // from the backend and cover every member of the team.
                const visibleRows = hideInactive
                  ? team.rows.filter((row) => !isInactiveRow(row))
                  : team.rows;
                return (
                <React.Fragment key={team.managerId || 'unassigned'}>
                  <TeamHeaderRow
                    managerId={team.managerId}
                    managerName={team.managerName}
                    rowCount={team.rows.length}
                  />
                  {visibleRows.length === 0 ? (
                    <EmptyTeamRow
                      label={team.rows.length === 0
                        ? (range
                          ? 'No funnel activity from this team in the date range.'
                          : undefined)
                        : 'All members of this team are hidden by the no-activity filter.'}
                    />
                  ) : (
                    visibleRows.map((row) => (
                      <MemberRow
                        key={rowId(team, row)}
                        row={row}
                        rowId={rowId(team, row)}
                        isOpen={openRows.has(rowId(team, row))}
                        onToggle={onToggle}
                        nextDates={location.nextDates}
                        reportDate={reportDate}
                        reportDateIsToday={reportDateIsToday}
                      />
                    ))
                  )}
                  <TotalsRow
                    label={team.managerId ? `Team ${team.managerName} Subtotal` : 'Unassigned Subtotal'}
                    totals={team.subtotals}
                    activeLeadsUnavailable={activeLeadsUnavailable}
                  />
                </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <TotalsRow
                label={`${location.locationName} Total`}
                totals={location.totals}
                emphasis
                activeLeadsUnavailable={activeLeadsUnavailable}
              />
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
